import { useEffect } from 'react';
import { getEnv } from '../utils/env';
import {
  applyAppUpdateIfIdle,
  markAppUpdateAvailable,
  UNKNOWN_UPDATE_VERSION,
} from '../pwa/appUpdate';

/**
 * Fetches the latest deployed version from `version.json` and compares it to
 * the currently running version. A mismatch is handed to the silent update
 * policy, which reloads at the next moment that cannot interrupt the user.
 */
const checkForUpdate = async () => {
  try {
    const response = await fetch('/version.json', {
      cache: 'no-cache',
      headers: { 'Cache-Control': 'no-cache' },
    });

    if (!response.ok) {
      console.error('Failed to fetch version.json');
      return;
    }

    // SPA fallback returns index.html (with 200) when version.json no longer exists.
    // Getting HTML back IS proof of a version mismatch — the old static file is gone.
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      console.info(
        `version.json returned non-JSON (${contentType}) — new deployment detected`
      );
      markAppUpdateAvailable(UNKNOWN_UPDATE_VERSION);
      applyAppUpdateIfIdle('detected');
      return;
    }

    const data = await response.json();
    const deployedVersion = data.version;
    const currentVersion = getEnv('APP_VERSION');

    console.info(
      `Current Version: ${currentVersion}, Deployed Version: ${deployedVersion}`
    );

    if (deployedVersion && deployedVersion !== currentVersion) {
      markAppUpdateAvailable(String(deployedVersion));
      applyAppUpdateIfIdle('detected');
    }
  } catch (error) {
    console.error('Error checking for updates:', error);
  }
};

/**
 * Hook to automatically check for updates periodically.
 */
const useVersionCheck = () => {
  useEffect(() => {
    // A cached index.html can boot an old build; catch that straight away.
    checkForUpdate();

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkForUpdate();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Periodic check so a tab left open still picks up the new build; if it
    // completes while the tab is hidden the update applies immediately.
    const interval = setInterval(checkForUpdate, 5 * 60 * 1000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, []);
};

export default useVersionCheck;
